import {signal} from "@angular/core"
import {deepCopy} from "@cm/utils"
import {
    Operator,
    OperatorCanvasToolboxType,
    OperatorPanelComponentType,
    OperatorProcessingHints,
} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {RecolorPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/recolor/panel/recolor-panel.component"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeOperatorRecolor} from "@app/textures/texture-editor/operator-stack/operators/recolor/node/node"
import {AnyImageOpResultNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {TextureType} from "@generated"
import {computeSourceLabStats} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/compute-source-lab-stats"
import {getOrComputeCachedUpstreamValue} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/cached-upstream-value"

export class OperatorRecolor extends OperatorBase<TextureEditNodes.OperatorRecolor> {
    readonly panelComponentType: OperatorPanelComponentType = RecolorPanelComponent
    readonly canvasToolbox: OperatorCanvasToolboxType = null

    readonly type = "operator-recolor" as const

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorRecolor | null) {
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-recolor",
                enabled: true,
                ignoreTextureTypes: Object.values(TextureType).filter((textureType) => textureType !== TextureType.Diffuse),
                targetStats: {
                    l: {mean: 0.5, stddev: 0.25},
                    a: {mean: 0, stddev: 0.1},
                    b: {mean: 0, stddev: 0.1},
                },
                luminance: 1,
                colorIntensity: 1,
                fade: 1,
            },
        )
        this._pendingInitialTargetStats = node === null
    }

    // OperatorBase
    override dispose(): void {
        super.dispose()
    }

    // OperatorBase
    override invalidateUpstreamCaches(): void {
        this.node.sourceStatsCache = undefined
    }

    // OperatorBase
    async clone(): Promise<Operator> {
        const clonedOperator = new OperatorRecolor(this.callback, deepCopy(this.node))
        await clonedOperator.init()
        return clonedOperator
    }

    // OperatorBase
    protected override async createImageOpGraph(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode> {
        // sourceStats is only eagerly computed/cached for interactive preview — for baking (final)
        // it's left undefined and ImageOpNodeOperatorRecolor computes it live instead, folded into
        // whatever imgproc evaluation is already running rather than forcing a separate
        // client-side download+evaluate of every texture type's source image on every submit.
        let sourceStats: TextureEditNodes.OperatorRecolor["targetStats"] | undefined
        if (hints.mode === "preview") {
            const {value, cache} = await getOrComputeCachedUpstreamValue(hints.textureType, this.node.sourceStatsCache, () =>
                computeSourceLabStats(this.callback.imageOpSystem, sourceImage),
            )
            sourceStats = value
            this.node.sourceStatsCache = cache
        }

        if (sourceStats && this._pendingInitialTargetStats) {
            this._pendingInitialTargetStats = false
            this.setTargetStats(deepCopy(sourceStats))
        }

        return imageOpNodeBag.getNode("recolor", ImageOpNodeOperatorRecolor, {
            sourceImage,
            targetStats: this.node.targetStats,
            sourceStats,
            luminance: this.node.luminance,
            colorIntensity: this.node.colorIntensity,
            fade: this.node.fade,
        })
    }

    private readonly _$targetMeanL = signal(this.node.targetStats.l.mean)
    readonly $targetMeanL = this._$targetMeanL.asReadonly()
    setTargetMeanL(value: number): void {
        if (this.node.targetStats.l.mean === value) {
            return
        }
        this.node.targetStats.l.mean = value
        this._$targetMeanL.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetStddevL = signal(this.node.targetStats.l.stddev)
    readonly $targetStddevL = this._$targetStddevL.asReadonly()
    setTargetStddevL(value: number): void {
        if (this.node.targetStats.l.stddev === value) {
            return
        }
        this.node.targetStats.l.stddev = value
        this._$targetStddevL.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetMeanA = signal(this.node.targetStats.a.mean)
    readonly $targetMeanA = this._$targetMeanA.asReadonly()
    setTargetMeanA(value: number): void {
        if (this.node.targetStats.a.mean === value) {
            return
        }
        this.node.targetStats.a.mean = value
        this._$targetMeanA.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetStddevA = signal(this.node.targetStats.a.stddev)
    readonly $targetStddevA = this._$targetStddevA.asReadonly()
    setTargetStddevA(value: number): void {
        if (this.node.targetStats.a.stddev === value) {
            return
        }
        this.node.targetStats.a.stddev = value
        this._$targetStddevA.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetMeanB = signal(this.node.targetStats.b.mean)
    readonly $targetMeanB = this._$targetMeanB.asReadonly()
    setTargetMeanB(value: number): void {
        if (this.node.targetStats.b.mean === value) {
            return
        }
        this.node.targetStats.b.mean = value
        this._$targetMeanB.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$targetStddevB = signal(this.node.targetStats.b.stddev)
    readonly $targetStddevB = this._$targetStddevB.asReadonly()
    setTargetStddevB(value: number): void {
        if (this.node.targetStats.b.stddev === value) {
            return
        }
        this.node.targetStats.b.stddev = value
        this._$targetStddevB.set(value)
        this.markEdited()
        this.requestEval()
    }

    // Best-effort: only populated once a preview eval has run for this texture type (and cleared by
    // invalidateUpstreamCaches() whenever anything upstream changes), so this may be undefined.
    getSourceStatsForCurrentTextureType(): TextureEditNodes.OperatorRecolor["targetStats"] | undefined {
        const textureType = this.callback.textureEditorData.textureTypeSpecific?.textureType ?? TextureType.Diffuse
        return this.node.sourceStatsCache?.[textureType]
    }

    setTargetStats(targetStats: TextureEditNodes.OperatorRecolor["targetStats"]): void {
        this.node.targetStats = targetStats
        this._$targetMeanL.set(targetStats.l.mean)
        this._$targetStddevL.set(targetStats.l.stddev)
        this._$targetMeanA.set(targetStats.a.mean)
        this._$targetStddevA.set(targetStats.a.stddev)
        this._$targetMeanB.set(targetStats.b.mean)
        this._$targetStddevB.set(targetStats.b.stddev)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$luminance = signal(this.node.luminance)
    readonly $luminance = this._$luminance.asReadonly()
    setLuminance(value: number): void {
        if (this.node.luminance === value) {
            return
        }
        this.node.luminance = value
        this._$luminance.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$colorIntensity = signal(this.node.colorIntensity)
    readonly $colorIntensity = this._$colorIntensity.asReadonly()
    setColorIntensity(value: number): void {
        if (this.node.colorIntensity === value) {
            return
        }
        this.node.colorIntensity = value
        this._$colorIntensity.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$fade = signal(this.node.fade)
    readonly $fade = this._$fade.asReadonly()
    setFade(value: number): void {
        if (this.node.fade === value) {
            return
        }
        this.node.fade = value
        this._$fade.set(value)
        this.markEdited()
        this.requestEval()
    }

    // True for a freshly created operator (no saved node yet) until createImageOpGraph() has had a chance to
    // seed targetStats from the operator's own source stats, so recoloring is a no-op until the user picks a
    // reference image.
    private _pendingInitialTargetStats: boolean
}
